import { useCallback, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/app/AuthContext.jsx';
import { useDocumentTitle, useLedgerQuery } from '@/hooks';
import { PageHead, Tabs, useConfirm, useToast, Skel } from '@/ui';
import {
  getUnmatchedPayments,
  getWebhookEvents,
  reprocessWebhookEvent,
} from '@/lib/recepthor-api';
import { qk } from '@/lib/queryKeys';
import { StatusDotPill } from '@/shared';
import { formatCLP, formatChileanDateTime } from '@/lib';

import './PlataformaPagos.css';
import './PlataformaEventos.css';

// Eventos · Plataforma (backoffice) — what the provider sent and what became of
// it. Two tabs, because they are one operator asking two questions:
//   · Eventos: every delivery with its processing outcome and its raw body, plus
//     the reprocess for a row that did not apply.
//   · Sin emparejar: the intents the provider charged whose invoice matched no
//     deal. Every row is money that came in and that nothing applied.
// The second is the result of the first: an event that processed and found
// nobody to apply to leaves a row there.

const EVENTS_KEY = qk.webhookEvents.key;

// El backoffice lee estas marcas para ordenar dos pasos del mismo día y para
// cruzar una fila contra el panel del proveedor, así que llevan hora, segundos
// y la zona de Chile. Una fecha sola no contesta ninguna de las dos preguntas.
const fmtDate = (iso) => formatChileanDateTime(iso) || '—';

// The event's five statuses, as the API declares them. RECEIVED is the default
// on insert; DUPLICATE is a re-delivery whose effect is already applied;
// PROCESSED is terminal; FAILED and IGNORED are the two a reprocess attacks.
const EVENT_STATUS = {
  RECEIVED: { label: 'Recibido', tone: 'pay' },
  PROCESSED: { label: 'Procesado', tone: 'ok' },
  DUPLICATE: { label: 'Duplicado', tone: 'info' },
  IGNORED: { label: 'Ignorado', tone: 'warn' },
  FAILED: { label: 'Falló', tone: 'err' },
};
const EVENT_FILTERS = [
  { key: '', label: 'Todos' },
  { key: 'FAILED', label: 'Fallidos' },
  { key: 'IGNORED', label: 'Ignorados' },
  { key: 'RECEIVED', label: 'Recibidos' },
  { key: 'PROCESSED', label: 'Procesados' },
  { key: 'DUPLICATE', label: 'Duplicados' },
];

// The unmatched-money union. Only UNMATCHED has a writer today: nothing
// produces the transitions to the other two yet, so this tab draws no filter for
// a status that cannot arrive. The labels are kept so a later row reads right.
const UNMATCHED_STATUS = {
  UNMATCHED: { label: 'Sin emparejar', tone: 'err' },
  RESOLVED: { label: 'Conciliada', tone: 'ok' },
  IGNORED: { label: 'Descartada', tone: 'info' },
};

function Field({ label, value, mono }) {
  return (
    <div className="plp-cf">
      <span className="plp-cf__l">{label}</span>
      <span className={mono ? 'plp-cf__s' : 'plp-cf__v'}>{value || '—'}</span>
    </div>
  );
}

// The event body is drawn as opaque JSON and not as named fields: the API types
// it as string keys to unknown values, so any shape this view assumed would be
// one nobody serves. What the operator needs from it is to read it, which is the
// question the card exists to answer.
function rawText(payload) {
  if (payload == null) return null;
  try {
    return JSON.stringify(payload, null, 2);
  } catch {
    // A circular reference or a non-serializable value must not take the row
    // down: the rest of the event stays readable without its body.
    return null;
  }
}

function EventCard({ e, onReprocess, busy, disabled }) {
  const st = EVENT_STATUS[e.status] ?? { label: e.status, tone: 'info' };
  const [open, setOpen] = useState(false);
  const raw = rawText(e.rawPayload);
  return (
    <article className="plp-card">
      <div className="plp-card__head">
        <div className="plp-card__lead">
          <span className="plp-date">{fmtDate(e.createdAt)}</span>
          <StatusDotPill status={st.tone} label={st.label} />
          <span className="plp-cf__v">{e.eventType}</span>
        </div>
        <div className="plp-card__right">
          <span className="plp-cf__s">{e.provider}</span>
          <span className="plp-actions">
            <button
              type="button"
              className="plp-rowbtn"
              disabled={disabled}
              onClick={() => onReprocess(e)}
            >
              {busy ? 'Reprocesando…' : 'Reprocesar'}
            </button>
          </span>
        </div>
      </div>
      <div className="plp-card__fields">
        <Field label="Referencia del proveedor" value={e.externalId} mono />
        <Field label="Procesado" value={fmtDate(e.processedAt)} />
      </div>
      {/* Collapsed per row: a page holds dozens of bodies, and open by default
          none of them is readable. */}
      <div className="plp-raw">
        <button
          type="button"
          className="plp-rowbtn plp-rowbtn--ghost"
          aria-expanded={open}
          disabled={raw == null}
          onClick={() => setOpen((v) => !v)}
        >
          {raw == null
            ? 'Sin cuerpo legible'
            : open
              ? 'Ocultar el cuerpo'
              : 'Ver el cuerpo'}
        </button>
        {open && raw != null && <pre className="plp-raw__body">{raw}</pre>}
      </div>
    </article>
  );
}

function UnmatchedCard({ p }) {
  const st = UNMATCHED_STATUS[p.status] ?? { label: p.status, tone: 'info' };
  return (
    <article className="plp-card">
      <div className="plp-card__head">
        <div className="plp-card__lead">
          <span className="plp-date">{fmtDate(p.createdAt)}</span>
          <StatusDotPill status={st.tone} label={st.label} />
          <span className="plp-cf__v">{p.provider}</span>
        </div>
        <div className="plp-card__right">
          {/* The provider reports the per-intent amount, and it can arrive empty
              or in another currency. It is formatted as pesos only when the row
              says so. */}
          <span className="plp-money">
            {p.paymentAmount == null
              ? '—'
              : p.currency && p.currency !== 'CLP'
                ? `${p.paymentAmount} ${p.currency}`
                : formatCLP(p.paymentAmount)}
          </span>
        </div>
      </div>
      <div className="plp-card__fields">
        <Field label="Factura del proveedor" value={p.invoiceId} mono />
        <Field label="Intento" value={p.intentId} mono />
        <Field label="Transacción" value={p.transactionId} mono />
        <Field
          label="Fecha de la transacción"
          value={fmtDate(p.transactionDate)}
        />
      </div>
    </article>
  );
}

export default function PlataformaEventos() {
  useDocumentTitle('Eventos · Plataforma');
  const { setBreadcrumb, setPills } = useOutletContext();
  const organizationId = useAuth()?.organizationId ?? null;
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const toast = useToast();

  const [tab, setTab] = useState('eventos');
  const [status, setStatus] = useState('');
  const [busyId, setBusyId] = useState(null);

  const events = useLedgerQuery({
    resourceKey: EVENTS_KEY,
    fetcher: getWebhookEvents,
    params: status ? { status } : {},
    pageSize: 50,
    surfaceErrors: true,
  });

  // Unpaginated: the endpoint answers the whole array, and the reconciliation
  // queue is short by definition. The rejection propagates on purpose, so a
  // failed read never renders as "there is no unmatched money".
  const unmatched = useQuery({
    queryKey: qk.unmatchedPayments(),
    queryFn: () => getUnmatchedPayments({ organizationId }),
    enabled: organizationId != null,
  });
  const unmatchedItems = Array.isArray(unmatched.data) ? unmatched.data : [];

  useEffect(() => {
    setBreadcrumb(['Eventos']);
    setPills([]);
    return () => {
      setBreadcrumb([]);
      setPills([]);
    };
  }, [setBreadcrumb, setPills]);

  const reprocessMut = useMutation({
    mutationFn: (e) => reprocessWebhookEvent(e.id, { organizationId }),
    onMutate: (e) => setBusyId(e.id),
    onSuccess: () => {
      toast({ kind: 'success', message: 'Evento reprocesado.' });
      // The row changes status, and a reprocess can match money that was
      // unmatched a moment earlier, so both tabs are reread.
      queryClient.invalidateQueries({ queryKey: qk.webhookEvents.all() });
      queryClient.invalidateQueries({ queryKey: qk.unmatchedPayments() });
    },
    // Backoffice is internal use: the API's message is shown as worded, which
    // is what the rest of the panel already does.
    onError: (e) =>
      toast({
        kind: 'err',
        title: 'No se pudo reprocesar el evento',
        message: e?.message ?? 'Error desconocido.',
      }),
    onSettled: () => setBusyId(null),
  });

  const onReprocess = useCallback(
    async (e) => {
      const ok = await confirm({
        title: 'Reprocesar el evento',
        body: (
          <>
            Se vuelve a pasar el cuerpo guardado de <b>{e.eventType}</b> (
            {e.externalId ?? 'sin referencia'}) por el mismo camino que lo
            recibió la primera vez, así que no puede aplicar nada distinto de lo
            que el evento decía. Si su efecto <b>ya está aplicado</b>,
            reprocesarlo no lo duplica: no cambia ninguna fila.
          </>
        ),
        confirmLabel: 'Reprocesar',
        confirmVariant: 'seal',
      });
      if (ok) reprocessMut.mutate(e);
    },
    [confirm, reprocessMut],
  );

  const skeleton = (
    <div className="plp-cards">
      {[0, 1, 2].map((i) => (
        <div key={i} className="plp-card">
          <div className="plp-card__head">
            <div className="plp-card__lead" style={{ gap: 12 }}>
              <Skel w={60} />
              <Skel w={80} />
            </div>
            <div className="plp-card__right">
              <Skel w={70} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const tabItems = [
    {
      key: 'eventos',
      label: 'Eventos',
      count: events.total,
      active: tab === 'eventos',
    },
    {
      key: 'sin-emparejar',
      label: 'Sin emparejar',
      count: unmatched.isSuccess ? unmatchedItems.length : undefined,
      active: tab === 'sin-emparejar',
    },
  ];

  return (
    <div className="page reveal">
      <PageHead
        kicker="Plataforma"
        title="Eventos"
        sub={<>Lo que mandó el proveedor y qué pasó con ello</>}
      />

      <div className="plp-tabs">
        <Tabs items={tabItems} onChange={(_i, item) => setTab(item.key)} />
      </div>

      {tab === 'eventos' && (
        <section className="plp-franja">
          <div className="plp-franja__cap">
            <span className="plp-evfilters">
              {EVENT_FILTERS.map((f) => (
                <button
                  key={f.key || 'todos'}
                  type="button"
                  className={`plp-evfilter${status === f.key ? ' is-on' : ''}`}
                  aria-pressed={status === f.key}
                  onClick={() => setStatus(f.key)}
                >
                  {f.label}
                </button>
              ))}
            </span>
          </div>
          {events.isError ? (
            <div className="plp-empty is-err">
              No se pudieron cargar los eventos.{' '}
              <button
                type="button"
                className="plp-morebtn"
                onClick={() => events.refetch()}
              >
                Reintentar
              </button>
            </div>
          ) : events.isLoading ? (
            skeleton
          ) : events.items.length === 0 ? (
            <div className="plp-empty">
              {status
                ? 'Ningún evento en ese estado.'
                : 'El proveedor todavía no ha mandado ningún evento.'}
            </div>
          ) : (
            <>
              <div className="plp-cards">
                {events.items.map((e) => (
                  <EventCard
                    key={e.id}
                    e={e}
                    onReprocess={onReprocess}
                    busy={busyId === e.id}
                    disabled={reprocessMut.isPending}
                  />
                ))}
              </div>
              {events.hasNextPage && (
                <div className="plp-more">
                  <button
                    type="button"
                    className="plp-morebtn"
                    disabled={events.isFetchingNextPage}
                    onClick={() => events.fetchNextPage()}
                  >
                    {events.isFetchingNextPage
                      ? 'Cargando…'
                      : `Cargar más (${events.items.length} de ${events.total})`}
                  </button>
                </div>
              )}
            </>
          )}
        </section>
      )}

      {tab === 'sin-emparejar' && (
        <section className="plp-franja">
          {unmatched.isPending && skeleton}
          {unmatched.isError && (
            <div className="plp-empty is-err">
              No se pudo cargar la plata sin emparejar.{' '}
              <button
                type="button"
                className="plp-morebtn"
                onClick={() => unmatched.refetch()}
              >
                Reintentar
              </button>
            </div>
          )}
          {unmatched.isSuccess && unmatchedItems.length === 0 && (
            <div className="plp-empty">
              No hay plata sin emparejar: todo lo que cobró el proveedor calzó
              con una cotización.
            </div>
          )}
          {unmatched.isSuccess && unmatchedItems.length > 0 && (
            <div className="plp-cards">
              {unmatchedItems.map((p) => (
                <UnmatchedCard key={p.id} p={p} />
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}
